/**
 * "TEST DATA" on any record carrying the backend's `test_data` marker.
 *
 * WHY IT EXISTS. Five test expenses survived into the handover inventory
 * because the convention for marking test rows was to name them "XXX123" — a
 * convention that is invisible to a query and, worse, invisible to a rep. A
 * Sales Officer looking at a queue could not tell a tester's row from her own.
 *
 * So the marker is a field (`test_data`, holding the name of the seed path
 * that wrote the row) and this is the ONE component that renders it. One
 * component per fact: if a marked row appears on a card, a table and a detail
 * page, all three render this, and there is one place to change what a test
 * row looks like.
 *
 * IT RENDERS NOTHING FOR A REAL RECORD. Absence of the field is the normal,
 * overwhelming case — every genuine row — so the component returns null rather
 * than an empty badge. That direction matters: a marker that is missing must
 * never be drawn as "real", it is simply not drawn at all.
 *
 * The origin is shown in the title attribute rather than the label, because
 * the rep needs "this is not real" at a glance and whoever is debugging needs
 * "which seeder made it" on hover.
 */
export default function TestDataBadge({ record, className = "" }) {
  const origin = record?.test_data;
  if (!origin) return null;

  return (
    <span
      className={`inline-flex items-center rounded border border-amber-500 bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-amber-900 ${className}`}
      data-testid={`test-data-${record.id ?? "row"}`}
      title={`Test data, created by ${origin}. This is not a real record.`}
    >
      Test data
    </span>
  );
}
